/**
 * Build Groups: erection sequences (S01..) and floors (F01..) per job. CR-2026-BUILD-GROUPS (2026-10-14).
 * Server: /api/build-groups. Spec §14; shipping (§15) and the Quality Data Package (D3) work by build group.
 * Rules are the server's: numbers are never reused, one group per piece, moving a piece out needs a reason,
 * shipped marks do not move, groups are never deleted (retire an empty one).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Card, Checkbox, Chip, Container, FormControlLabel, Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission } from "../../utils/hasPermission";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;
const when = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "");

/** The POST /build-groups/assign body (exported for tests). groupId null takes pieces out. */
export function assignBody(jobId, groupId, { itemIds = [], pieceIds = [] }, reason) {
  const b = { jobId: Number(jobId), groupId: groupId == null || groupId === "" ? null : Number(groupId) };
  if (itemIds.length) b.itemIds = itemIds.map(Number);
  if (pieceIds.length) b.pieceIds = pieceIds.map(Number);
  if ((reason || "").trim()) b.reason = reason.trim();
  return b;
}

export default function BuildGroups() {
  const [params, setParams] = useSearchParams();
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const canEdit = hasPermission(perms, "edit_job");
  const jobId = params.get("jobId") || "";
  const groupId = params.get("groupId") || "";
  const [jobs, setJobs] = useState([]);
  const [data, setData] = useState({ groups: [], unassignedPieces: 0 });
  const [marks, setMarks] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [selMarks, setSelMarks] = useState([]);
  const [selPieces, setSelPieces] = useState([]);
  const [reason, setReason] = useState("");
  const [newType, setNewType] = useState("SEQUENCE");
  const [newName, setNewName] = useState("");
  const [moves, setMoves] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const group = data.groups.find((g) => String(g.id) === String(groupId)) || null;
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); if (k === "jobId") p.delete("groupId"); setParams(p); };

  useEffect(() => { ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || r.data?.data || [])).catch(() => setJobs([])); }, []);
  const loadGroups = useCallback(async () => {
    if (!jobId) { setData({ groups: [], unassignedPieces: 0 }); return; }
    try { const r = await ApiCall.get(`/build-groups?jobId=${jobId}`); setData(r.data.data); } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load build groups.") }); }
  }, [jobId]);
  useEffect(() => { loadGroups(); setMoves(null); }, [loadGroups]);
  useEffect(() => {
    setMarks([]);
    if (!jobId) return;
    ApiCall.get(`/fabricated-items/job/${jobId}`).then((r) => setMarks(Array.isArray(r.data?.data) ? r.data.data : [])).catch(() => setMarks([]));
  }, [jobId]);
  const loadPieces = useCallback(async () => {
    setPieces([]); setSelPieces([]);
    if (!groupId) return;
    try { const r = await ApiCall.get(`/build-groups/${groupId}/pieces`); setPieces(r.data.data.pieces || []); } catch (_) { setPieces([]); }
  }, [groupId]);
  useEffect(() => { loadPieces(); setSelMarks([]); setReason(""); }, [loadPieces]);

  const run = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { const r = await fn(); setMsg({ severity: "success", text: r?.data?.message || okText }); await loadGroups(); await loadPieces(); setSelMarks([]); setSelPieces([]); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not saved.") }); } finally { setBusy(false); }
  };
  const create = () => run(() => ApiCall.post("/build-groups", { jobId: Number(jobId), type: newType, name: newName.trim() || undefined }).then((r) => { setNewName(""); return r; }));
  const assignMarks = () => run(() => ApiCall.post("/build-groups/assign", assignBody(jobId, groupId, { itemIds: selMarks }, reason)));
  const removePieces = () => run(() => ApiCall.post("/build-groups/assign", assignBody(jobId, null, { pieceIds: selPieces }, reason)));
  const retire = () => run(() => ApiCall.post(`/build-groups/${groupId}/retire`, { reason }));
  const showMoves = async () => { try { const r = await ApiCall.get(`/build-groups/moves?jobId=${jobId}`); setMoves(r.data.data); } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load history.") }); } };
  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <>
      <Helmet><title>Build groups | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4">Build groups</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Erection sequences (S01, S02 ...) and floors (F01 ...) per job. Shipping and the Quality Data Package are organised by build group.
        </Typography>
        <TextField select size="small" label="Job" value={jobId} onChange={(e) => set("jobId", e.target.value)} sx={{ minWidth: 300, mb: 2 }}>
          <MenuItem value="">Choose a job</MenuItem>
          {jobs.map((j) => <MenuItem key={j.id} value={String(j.id)}>{j.name}</MenuItem>)}
        </TextField>
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}
        {jobId && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 2 }}>
                <Typography variant="h6">Groups</Typography>
                <Typography variant="caption" color={data.unassignedPieces ? "error" : "text.secondary"}>{data.unassignedPieces} piece(s) not in any group</Typography>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableBody>
                    {data.groups.length === 0 && <TableRow><TableCell><Typography variant="caption">No build groups yet.</Typography></TableCell></TableRow>}
                    {data.groups.map((g) => (
                      <TableRow key={g.id} hover selected={String(g.id) === String(groupId)} onClick={() => set("groupId", String(g.id))} sx={{ cursor: "pointer" }}>
                        <TableCell sx={{ fontWeight: 600 }}>{g.code}</TableCell>
                        <TableCell>{g.name || ""}</TableCell>
                        <TableCell align="right">{g.pieces}</TableCell>
                        <TableCell>{g.status === "RETIRED" && <Chip size="small" label="Retired" />}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {canEdit && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <TextField select size="small" value={newType} onChange={(e) => setNewType(e.target.value)} sx={{ width: 130 }}>
                      <MenuItem value="SEQUENCE">Sequence</MenuItem><MenuItem value="FLOOR">Floor</MenuItem>
                    </TextField>
                    <TextField size="small" placeholder="Name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} />
                    <Button variant="contained" disabled={busy} onClick={create}>Add</Button>
                  </Stack>
                )}
                <Button size="small" sx={{ mt: 1 }} onClick={showMoves}>Show assignment history</Button>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              {group ? (
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6">{group.code}{group.name ? `: ${group.name}` : ""} ({group.pieces} pieces)</Typography>
                  {group.status === "RETIRED" && <Alert severity="info" sx={{ my: 1 }}>Retired {when(group.retiredAt)} by {group.retiredByName}: {group.retireReason}</Alert>}
                  {canEdit && group.status === "ACTIVE" && (
                    <>
                      <TextField fullWidth size="small" sx={{ my: 1.5 }} label="Reason (needed to move pieces from another group, take pieces out, or retire)" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <Typography variant="subtitle2">Add marks (all their active pieces)</Typography>
                      <Box sx={{ maxHeight: 220, overflow: "auto", border: 1, borderColor: "divider", p: 1, mb: 1 }}>
                        {marks.map((m) => (
                          <FormControlLabel key={m.id} label={`${m.name} (qty ${m.quantity})`}
                            control={<Checkbox size="small" checked={selMarks.includes(m.id)} onChange={() => toggle(selMarks, setSelMarks, m.id)} />} />
                        ))}
                      </Box>
                      <Button variant="contained" disabled={busy || !selMarks.length} onClick={assignMarks}>Add {selMarks.length} mark(s) to {group.code}</Button>
                    </>
                  )}
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Pieces in {group.code}</Typography>
                  <SmartTable tableId="build-group-pieces" lockedColumns={["Piece", "Identity"]} size="small">
                    <TableHead><TableRow>{canEdit && <TableCell padding="checkbox" />}<TableCell>Piece</TableCell><TableCell>Identity</TableCell></TableRow></TableHead>
                    <TableBody>
                      {pieces.length === 0 && <TableRow><TableCell colSpan={3}><Typography variant="caption">None.</Typography></TableCell></TableRow>}
                      {pieces.map((p) => (
                        <TableRow key={p.id}>
                          {canEdit && <TableCell padding="checkbox"><Checkbox size="small" checked={selPieces.includes(p.id)} onChange={() => toggle(selPieces, setSelPieces, p.id)} /></TableCell>}
                          <TableCell>{p.mark} {p.display}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{p.publicId}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </SmartTable>
                  {canEdit && group.status === "ACTIVE" && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Button color="warning" disabled={busy || !selPieces.length} onClick={removePieces}>Take {selPieces.length} piece(s) out</Button>
                      <Button color="error" disabled={busy || group.pieces > 0} onClick={retire}>Retire {group.code}</Button>
                    </Stack>
                  )}
                </Card>
              ) : (
                <Typography variant="body2" color="text.secondary">Choose a group, or add one.</Typography>
              )}
              {moves && (
                <Card sx={{ p: 2, mt: 2 }}>
                  <Typography variant="h6">Assignment history ({moves.length})</Typography>
                  <SmartTable tableId="build-group-moves" lockedColumns={["When", "Piece", "From", "To"]} size="small">
                    <TableHead><TableRow><TableCell>When</TableCell><TableCell>Piece</TableCell><TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Reason</TableCell><TableCell>By</TableCell></TableRow></TableHead>
                    <TableBody>
                      {moves.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>{when(m.movedAt)}</TableCell><TableCell sx={{ fontFamily: "monospace" }}>{m.publicId}</TableCell>
                          <TableCell>{m.from || "none"}</TableCell><TableCell>{m.to || "none"}</TableCell><TableCell>{m.reason || ""}</TableCell><TableCell>{m.movedByName || ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </SmartTable>
                </Card>
              )}
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
}
